import {
  Voicemail,
  ArrowDownLeft,
  ArrowUpRight,
  Trash2,
  Reply,
  AlertCircle,
  Clock,
  CheckCircle,
  Phone,
  MessageSquare,
  Loader2
} from "lucide-react";
import type React from "react";
import { useState } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { formatRelativeTime, formatDuration } from "@/app/_module/utils/formatters";
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { toast } from "@/app/_module/components/ui/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { callService } from "@/app/_module/services/callService";

import { VoicemailPlayer } from "./VoicemailPlayer";
import { ViewLeadButton } from "./ViewLeadButton";
import ViewerDisabled from "@/app/_module/components/ViewerDisabled";

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "Unknown";
  const cleaned = phone.replace(/\D/g, '');
  // Remove leading 1 if present (US country code)
  const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone; // Return original if not a 10-digit number
};

interface VoicemailRecord {
  id: string;
  leadId: string;
  phoneNumber: string;
  contactName?: string;
  direction: "inbound" | "outbound";
  duration: number;
  audioUrl: string;
  transcript?: string;
  isListened: boolean;
  timestamp: Date;
  callId?: string;
  notes?: string;
  priority: "low" | "normal" | "high";
}

interface VoicemailListProps {
  voicemails: VoicemailRecord[];
  type: "received" | "sent";
  onMarkAsListened: (id: string) => void;
  onDelete: (id: string) => void | Promise<void>;
  onReply: (voicemail: VoicemailRecord) => void;
}

export const VoicemailList: React.FC<VoicemailListProps> = function({
  voicemails,
  type,
  onMarkAsListened,
  onDelete,
  onReply
}) {
  const [expandedVoicemail, setExpandedVoicemail] = useState<string | null>(null);
  const [, setPlayingVoicemail] = useState<string | null>(null);
  const [callingId, setCallingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Hooks for call and SMS functionality
  const { data: phoneNumbers = [] } = useUserPhoneNumbers();
  const { user } = useUserContext();
  const { activeSmsConversation, startConversation, maximizeConversation } = useSmsConversation();
  const {
    isDeviceReady,
    isDeviceRegistering,
    connect,
    forwardCallsToPhone,
  } = useTwilioDevice();

  // Get user's default outbound phone from profile
  const userDefaultPhone = (user?.user as unknown as Record<string, unknown>)?.defaultOutboundPhone as string;

  // Select best caller ID using area code matching
  const selectBestCallerId = (leadPhone: string): string | undefined => {
    if (userDefaultPhone) {
      return userDefaultPhone;
    }

    const leadAreaCode = leadPhone.replace(/\D/g, '').slice(0, 3);
    const matchingPhone = (phoneNumbers as any[]).find((phone: any) => {
      const phoneAreaCode = phone.phoneNumber?.replace(/\D/g, '').slice(0, 3);
      return phoneAreaCode === leadAreaCode;
    });

    if (matchingPhone) {
      return matchingPhone.phoneNumber;
    }

    if (phoneNumbers.length > 0) {
      return (phoneNumbers[0] as any).phoneNumber;
    }

    return undefined;
  };

  // Handle call button click - same logic as LeadActionButtons
  const handleCall = async (voicemail: VoicemailRecord) => {
    if (!voicemail.phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "This voicemail does not have a phone number.",
        variant: "destructive",
      });
      return;
    }

    if (phoneNumbers.length === 0) {
      toast({
        title: "No Active Phone Numbers",
        description: "You need at least one active phone number to make calls. Go to Settings > Phone Numbers to purchase one.",
        variant: "destructive",
      });
      return;
    }

    setCallingId(voicemail.id);

    try {
      if (forwardCallsToPhone) {
        // Use phone-to-phone method (call forwarding)
        await api.post({
          url: getApiUrl('/communication/sessions/manual-call'),
          body: {
            toNumber: voicemail.phoneNumber,
            leadId: voicemail.leadId || undefined,
          },
          auth: true,
        });

        const result = await callService.initiateCall({
          to: voicemail.phoneNumber,
          from: undefined,
          lead_id: voicemail.leadId || undefined,
          recording_enabled: true,
          metadata: {
            lead_name: voicemail.contactName || '',
            source: "voicemail_callback_forwarding",
          }
        });

        if (result && result.call_id) {
          toast({
            title: "Call Forwarding",
            description: `Your phone will ring to connect to ${voicemail.contactName || formatPhoneNumber(voicemail.phoneNumber)}`,
          });
        }
      } else {
        // Use browser calling
        if (!isDeviceReady) {
          toast({
            title: "Device Not Ready",
            description: isDeviceRegistering ? "Initializing calling device..." : "Calling device is not ready. Please refresh the page or enable call forwarding in Settings.",
            variant: "destructive",
          });
          setCallingId(null);
          return;
        }

        // Request microphone permission before starting call
        const { requestMicrophoneWithGuidance } = await import("@/app/_module/utils/microphone-permission");
        const permissionResult = await requestMicrophoneWithGuidance();

        if (!permissionResult.granted) {
          toast({
            title: "Microphone Access Required",
            description: permissionResult.error || "Please allow microphone access to make calls.",
            variant: "destructive",
          });
          setCallingId(null);
          return;
        }

        // Create session record
        await api.post({
          url: getApiUrl('/communication/sessions/manual-call'),
          body: {
            toNumber: voicemail.phoneNumber,
            leadId: voicemail.leadId || undefined,
          },
          auth: true,
        });

        // Use Twilio Voice SDK browser calling
        // Backend smart area code matching selects the best caller ID per lead
        const call = await connect({
          to: voicemail.phoneNumber,
          leadId: voicemail.leadId || undefined,
          metadata: {
            lead_name: voicemail.contactName || '',
            source: "voicemail_callback",
          }
        });

        if (call) {
          toast({
            title: "Call Connected",
            description: `Calling ${voicemail.contactName || formatPhoneNumber(voicemail.phoneNumber)}`,
          });
        }
      }
    } catch (error) {
      toast({
        title: "Call Failed",
        description: "Failed to initiate call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCallingId(null);
    }
  };

  // Handle SMS button click - same logic as LeadActionButtons
  const handleSMS = async (voicemail: VoicemailRecord) => {
    if (!voicemail.phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "This voicemail does not have a phone number.",
        variant: "destructive",
      });
      return;
    }

    // Check if there's already an active SMS conversation with this phone number
    if (activeSmsConversation && activeSmsConversation.phoneNumber === voicemail.phoneNumber) {
      maximizeConversation();
      return;
    }

    // Check if there's SMS history in the database
    try {
      const { getSmsHistoryForPhone } = await import('@/app/(authenticated)/communication/_modules/services/smsHistoryService');
      const historyMessages = await getSmsHistoryForPhone(voicemail.phoneNumber);

      // Open widget with conversation (history exists or fresh start)
      await startConversation({
        leadId: voicemail.leadId || '',
        leadName: voicemail.contactName || formatPhoneNumber(voicemail.phoneNumber),
        phoneNumber: voicemail.phoneNumber,
      });
    } catch (error) {
      // Still try to open conversation even on error
      await startConversation({
        leadId: voicemail.leadId || '',
        leadName: voicemail.contactName || formatPhoneNumber(voicemail.phoneNumber),
        phoneNumber: voicemail.phoneNumber,
      });
    }
  };

  const getPriorityIndicator = (priority: VoicemailRecord["priority"]) => {
    if (priority === "high") {
      return <AlertCircle className="h-3 w-3 text-red-500" />;
    }
    return null;
  };

  const getPriorityBadge = (priority: VoicemailRecord["priority"]) => {
    if (priority === "high") {
      return (
        <Badge className="text-xs" variant="destructive">
          Urgent
        </Badge>
      );
    }
    return null;
  };

  const getDirectionIcon = (direction: VoicemailRecord["direction"]) => {
    return direction === "inbound" ? (
      <ArrowDownLeft className="h-4 w-4 text-blue-600" />
    ) : (
      <ArrowUpRight className="h-4 w-4 text-green-600" />
    );
  };

  const handleDelete = async (voicemailId: string) => {
    if (deletingId) return;
    setDeletingId(voicemailId);
    try {
      await onDelete(voicemailId);
    } finally {
      setDeletingId(null);
    }
  };

  const handleVoicemailClick = (voicemailId: string) => {
    if (expandedVoicemail === voicemailId) {
      setExpandedVoicemail(null);
      setPlayingVoicemail(null);
    } else {
      setExpandedVoicemail(voicemailId);
    }
  };

  const handlePlayStateChange = (voicemailId: string, isPlaying: boolean) => {
    setPlayingVoicemail(isPlaying ? voicemailId : null);
  };

  if (voicemails.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Voicemail className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No {type} voicemails found
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {voicemails.map((voicemail) => (
        <Card
          key={voicemail.id}
          className={`transition-all duration-200 cursor-pointer hover:shadow-md ${
            !voicemail.isListened && type === "received" 
              ? "border-blue-200 bg-blue-50/50" 
              : "hover:bg-accent/50"
          }`}
        >
          <CardContent className="p-4">
            <div 
              className="flex items-start justify-between"
              onClick={() => handleVoicemailClick(voicemail.id)}
            >
              <div className="flex items-start space-x-3 flex-1">
                <div className="flex items-center gap-1">
                  {getDirectionIcon(voicemail.direction)}
                  {!voicemail.isListened && type === "received" && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`text-sm truncate ${
                      !voicemail.isListened && type === "received"
                        ? "font-semibold text-foreground"
                        : "font-medium text-foreground"
                    }`}>
                      {type === "received" ? "From: " : "To: "}
                      {voicemail.contactName || formatPhoneNumber(voicemail.phoneNumber)}
                    </p>
                    {getPriorityIndicator(voicemail.priority)}
                    {getPriorityBadge(voicemail.priority)}
                    {!!voicemail.isListened && type === "received" && <CheckCircle className="h-3 w-3 text-green-500" />}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <span>{voicemail.contactName ? formatPhoneNumber(voicemail.phoneNumber) : "Unknown"}</span>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(voicemail.duration)}
                    </div>
                    <span>•</span>
                    <span>{formatRelativeTime(voicemail.timestamp)}</span>
                  </div>

                  {!!voicemail.transcript && expandedVoicemail !== voicemail.id && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {voicemail.transcript}
                    </p>
                  )}

                  {!!voicemail.notes && (
                    <p className="text-xs text-muted-foreground bg-gray-50 p-2 rounded mt-2">
                      Note: {voicemail.notes}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  {type === "received" && (
                    <>
                      <ViewerDisabled message="Upgrade to call back">
                        <Button
                          className="h-8 w-8 p-0"
                          onClick={() => handleCall(voicemail)}
                          size="sm"
                          variant="ghost"
                          title="Call back"
                          disabled={callingId === voicemail.id}
                        >
                          {callingId === voicemail.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Phone className="h-3 w-3" />
                          )}
                        </Button>
                      </ViewerDisabled>
                      <ViewerDisabled message="Upgrade to reply via SMS">
                        <Button
                          className="h-8 w-8 p-0"
                          onClick={() => handleSMS(voicemail)}
                          size="sm"
                          variant="ghost"
                          title="Reply via SMS"
                        >
                          <MessageSquare className="h-3 w-3" />
                        </Button>
                      </ViewerDisabled>
                    </>
                  )}
                  <ViewerDisabled message="Upgrade to delete voicemails">
                    <Button
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(voicemail.id)}
                      size="sm"
                      variant="ghost"
                      title="Delete voicemail"
                      disabled={deletingId === voicemail.id}
                    >
                      {deletingId === voicemail.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </ViewerDisabled>
                </div>
                {!!voicemail.leadId && (
                  <ViewLeadButton
                    leadId={voicemail.leadId}
                    contactName={voicemail.contactName || formatPhoneNumber(voicemail.phoneNumber)}
                  />
                )}
              </div>
            </div>

            {expandedVoicemail === voicemail.id && type === "received" && (
              <div className="mt-4 pt-4 border-t">
                <VoicemailPlayer
                  audioUrl={voicemail.audioUrl}
                  duration={voicemail.duration}
                  onPlayStateChange={(isPlaying) => handlePlayStateChange(voicemail.id, isPlaying)}
                  onMarkAsListened={!voicemail.isListened ? () => onMarkAsListened(voicemail.id) : undefined}
                  transcript={voicemail.transcript}
                />
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
